import React, {useState, useEffect, useCallback, useContext} from "react";
import {Form, Input, Select} from "antd";
import TableFilterControlsSection from "@common/TableFilterControlsSection";
import axios from "@common/axios";
import {debounce} from "lodash";

const getFilterOptions = (syllabusId)=>{
    return axios.get('/web/ui/test/teacher/getExamFilterOptions', {params: {syllabusId:syllabusId}})
        .then((response)=>{
            return response.data;
        }).catch((error)=>{
            console.error('Error fetching exam filters: ',error);
            return {};
        })
}

const changeObjectToOptionsArray = (obj)=>{
    return obj ? Object.entries(obj).map(([key, value]) => {return {key,value}}) : [];
}

const ExamFilters = props => {
    const {
        popupMatchSelectWidth= false,
        isAutoMark,
        onUpdate,
        syllabusId = null,
        onLoadComplete,
        handleQuestionLevelNameChange
    } = props;

    const [sourceOptions, setSourceOptions] = useState([]),
        [yearOptions, setYearOptions] = useState([]),
        [numberOptions, setNumberOptions] = useState([]),
        [sectionOptions, setSectionOptions] = useState([]),
        [difficultyLevelOptions, setDifficultyLevelOptions] = useState([]),
        [examDifficultyLabel, setExamDifficultyLabel] = useState('Difficulty'),
        [examQuestionSourceLabel, setExamQuestionSourceLabel] = useState('Source'),
        [answerTypeOptions, setAnswerTypeOptions] = useState([]),
        [calculatorOptions, setCalculatorOptions] = useState([]),
        [hideExamYear, setHideExamYear] = useState(false),
        [hideExamNumber, setHideExamNumber] = useState(false),
        [bandLevelOptions, setBandLevelOptions] = useState([]),
        [loadConfigComplete, setLoadConfigComplete] = useState(false);

    useEffect(()=>{
        if(syllabusId !== null){
            getFilterOptions(syllabusId).then(data =>{
                setSourceOptions(changeObjectToOptionsArray(data.examStyle));
                setYearOptions(changeObjectToOptionsArray(data.examYear));
                setNumberOptions(changeObjectToOptionsArray(data.examNumber));
                setSectionOptions(changeObjectToOptionsArray(data.section));
                setDifficultyLevelOptions(changeObjectToOptionsArray(data.questionLevel));
                setAnswerTypeOptions(changeObjectToOptionsArray(data.questionType));
                setCalculatorOptions(changeObjectToOptionsArray(data.calculator));
                setBandLevelOptions(changeObjectToOptionsArray(data.questionLevelNames)); //called bandLevelOptions here as it is originally implemented for HSC Exams
                handleQuestionLevelNameChange(data.questionLevelNames ? Object.values(data.questionLevelNames) : []);
                setExamDifficultyLabel(data.examDifficultyLabel?data.examDifficultyLabel:'Difficulty');
                setExamQuestionSourceLabel(data.examQuestionSourceLabel?data.examQuestionSourceLabel:'Source');
                setHideExamYear(data.hideExamYear?data.hideExamYear:false);
                setHideExamNumber(data.hideExamNumber?data.hideExamNumber:false);
            })
        }
    },[syllabusId]);

    const debouncedInputChange = useCallback(
        debounce((e) => {
            handleChange();
        }, 500), []);

    const onLoadConfigLoadComplete = ()=>{
        onLoadComplete?.();
    }

    useEffect(()=>{
        if(!loadConfigComplete) {
            onLoadConfigLoadComplete();
            setLoadConfigComplete(true);
        }
    },[loadConfigComplete])

    const handleChange = ()=>{
        onUpdate?.();
    }

    return [
        <TableFilterControlsSection key="exam-filters-1">
            <Form.Item
                label={examQuestionSourceLabel}
                name="examStyle"
                // required={true}
            >
                <Select onChange={handleChange} popupMatchSelectWidth={popupMatchSelectWidth}>
                    <Select.Option key={'All'}>All</Select.Option>
                    {sourceOptions.map((option)=>{
                        return <Select.Option key={option.key}>{option.value}</Select.Option>
                    })}
                </Select>
            </Form.Item>
            {!hideExamYear &&
                <Form.Item label="Exam year"
                       name="examYear"
                >
                    <Select onChange={handleChange} popupMatchSelectWidth={popupMatchSelectWidth}>
                        <Select.Option key={'All'}>All</Select.Option>
                        {yearOptions.map((option)=>{
                            return <Select.Option key={option.key}>{option.value}</Select.Option>
                        })}
                    </Select>
                </Form.Item>
            }
            {!hideExamNumber && <Form.Item label="Exam no."
                           name="examNumber"
                >
                    <Select onChange={handleChange} popupMatchSelectWidth={popupMatchSelectWidth}>
                        <Select.Option key={'All'}>All</Select.Option>
                        {numberOptions.map((option)=>{
                            return <Select.Option key={option.key}>{option.value}</Select.Option>;
                        })}
                    </Select>
                </Form.Item>
            }
            <Form.Item label="Exam section"
                       name="section"
            >
                <Select onChange={handleChange} popupMatchSelectWidth={popupMatchSelectWidth}>
                    <Select.Option key={'All'}>All</Select.Option>
                    {sectionOptions.map((option)=>{
                        return <Select.Option key={option.key}>{option.value}</Select.Option>;
                    })}
                </Select>
            </Form.Item>
        </TableFilterControlsSection>,
        <TableFilterControlsSection key="exam-filters-2" showDivider={false}>
            <Form.Item label={examDifficultyLabel}
                       name="testDifficultyLevel"
            >
                <Select onChange={handleChange} popupMatchSelectWidth={popupMatchSelectWidth}>
                    <Select.Option key={'All'}>All</Select.Option>
                    {bandLevelOptions.length === 0 && difficultyLevelOptions.map((option)=>{
                        return <Select.Option key={option.key}>Level {option.value}</Select.Option>;
                    })}
                    {bandLevelOptions.length > 0 && bandLevelOptions.map((option,idx)=>{
                        return <Select.Option key={(idx+1)}>{option.value}</Select.Option>;
                    })}
                </Select>
            </Form.Item>
            {/* Hide answer type for auto-marked/ONLINE exams */}
            {!isAutoMark && <Form.Item label="Answer type"
                       name="questionAnswerTypeGroup"
            >
                <Select onChange={handleChange} popupMatchSelectWidth={popupMatchSelectWidth}>
                   <Select.Option key={'All'}>All</Select.Option>
                    {/*{!isAutoMark && */}
                    {
                        answerTypeOptions.map((option)=>{
                        return <Select.Option key={option.key}>{option.value}</Select.Option>;
                        })
                    }
                {/*}*/}
                    {/*{isAutoMark && <Select.Option key={'MULTIPLE_CHOICE'}>Multiple-choice</Select.Option>}*/}
                </Select>
            </Form.Item>}
            {/* Hide calculator option if questions are all calculator or all not calculator */}
            {calculatorOptions.length > 0 && <Form.Item label="Calculator"
                           name="useCalculator"
                >
                    <Select onChange={handleChange} popupMatchSelectWidth={popupMatchSelectWidth}>
                        <Select.Option key={'All'}>All</Select.Option>
                        {calculatorOptions.map((option)=>{
                            return <Select.Option key={option.key}>{option.value}</Select.Option>;
                        })}
                    </Select>
                </Form.Item>
            }
            <Form.Item label="Keyword"
                       name="questionTags"
            >
                <Input onChange={debouncedInputChange}/>
            </Form.Item>
        </TableFilterControlsSection>
    ]
}

export default ExamFilters;